import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

// Firebase Functions
const validateUser = httpsCallable(functions, 'validateUser');
const createUserProfile = httpsCallable(functions, 'createUserProfile');
const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
const deleteUserData = httpsCallable(functions, 'deleteUserData');

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  preferences: {
    theme: 'light' | 'dark' | 'auto';
    defaultSampleRate: number;
    autoSave: boolean;
    notifications: boolean;
    enableWebGL?: boolean;
  };
  role: string;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
  lastLoginAt?: any;
}

class AuthService {
  private currentUser: User | null = null;
  private userProfile: UserProfile | null = null;
  private profileChangeListeners: ((profile: UserProfile | null) => void)[] = [];
  
  constructor() {
    onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      if (user) {
        await this.validateAndInitializeUser();
        // Notify listeners that profile has been loaded
        this.notifyProfileChange();
      } else {
        this.userProfile = null;
        this.notifyProfileChange();
      }
    });
  }

  private notifyProfileChange() {
    this.profileChangeListeners.forEach(listener => listener(this.userProfile));
  }

  // Google Sign In
  async signInWithGoogle(): Promise<User> {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await this.validateAndInitializeUser();
      return result.user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }

  // Email/Password Sign In
  async signInWithEmail(email: string, password: string): Promise<User> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await this.validateAndInitializeUser();
      return result.user;
    } catch (error) {
      console.error('Email sign-in error:', error);
      throw error;
    }
  }

  // Email/Password Sign Up
  async signUpWithEmail(email: string, password: string, displayName?: string): Promise<User> {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Create user profile immediately with the provided display name
      // Use a slight delay to ensure auth state is fully initialized
      await new Promise(resolve => setTimeout(resolve, 100));
      const profile = await this.createUserProfile({ displayName });
      this.userProfile = profile;
      return result.user;
    } catch (error) {
      console.error('Email sign-up error:', error);
      throw error;
    }
  }

  // Sign Out
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      this.userProfile = null;
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  }

  // Validate and initialize user profile
  private async validateAndInitializeUser(): Promise<void> {
    try {
      const result = await validateUser();
      const data = result.data as { profile?: UserProfile };
      // Only set profile if it exists (it may be null for brand new users)
      if (data.profile) {
        this.userProfile = data.profile;
      }
    } catch (error) {
      console.error('User validation error:', error);
    }
  }

  // Force refresh profile from Firestore
  async refreshProfile(): Promise<UserProfile | null> {
    await this.validateAndInitializeUser();
    return this.userProfile;
  }

  // Create user profile
  async createUserProfile(data: { displayName?: string; preferences?: any }): Promise<UserProfile> {
    try {
      const result = await createUserProfile(data);
      const responseData = result.data as { profile?: UserProfile };
      this.userProfile = responseData.profile;
      this.notifyProfileChange();
      return this.userProfile;
    } catch (error) {
      console.error('Create user profile error:', error);
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(data: { displayName?: string; preferences?: any }): Promise<UserProfile> {
    try {
      const result = await updateUserProfile(data);
      const responseData = result.data as { profile?: UserProfile };
      this.userProfile = responseData.profile;
      this.notifyProfileChange();
      return this.userProfile;
    } catch (error) {
      console.error('Update user profile error:', error);
      throw error;
    }
  }

  // Delete user data
  async deleteUserData(): Promise<void> {
    try {
      await deleteUserData();
      this.userProfile = null;
    } catch (error) {
      console.error('Delete user data error:', error);
      throw error;
    }
  }

  // Getters
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  getUserProfile(): UserProfile | null {
    return this.userProfile;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Auth state listener
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  }

  // Profile change listener
  onProfileChange(callback: (profile: UserProfile | null) => void): () => void {
    this.profileChangeListeners.push(callback);
    // Don't immediately call - let the auth state change handler provide initial state
    // This prevents issues when new subscribers mount during re-renders
    // Return unsubscribe function
    return () => {
      this.profileChangeListeners = this.profileChangeListeners.filter(l => l !== callback);
    };
  }
}

export default new AuthService();